import { useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";

/**
 * Hook to extract project identifiers from URL parameters
 * Similar to useWorkspaceIdentifiers but for project context
 */
export const useProjectIdentifiers = () => {
  const params = useParams();
  const location = useLocation();

  const projectId = useMemo(() => {
    // Try to get projectId from URL params
    const paramProjectId = params.projectId;
    if (paramProjectId) {
      return paramProjectId;
    }

    // Try to extract from pathname if not in params
    const pathSegments = location.pathname.split("/");
    const projectIndex = pathSegments.findIndex((segment) => segment === "project");
    if (projectIndex !== -1 && pathSegments[projectIndex + 1]) {
      return pathSegments[projectIndex + 1];
    }

    // Try to extract from pathname patterns like /dashboard/project/123
    const dashboardIndex = pathSegments.findIndex((segment) => segment === "dashboard");
    if (
      dashboardIndex !== -1 &&
      pathSegments[dashboardIndex + 1] === "project" &&
      pathSegments[dashboardIndex + 2]
    ) {
      return pathSegments[dashboardIndex + 2];
    }

    return null;
  }, [params.projectId, location.pathname]);

  const workspaceId = useMemo(() => {
    // Try to get workspaceId from URL params
    const paramWorkspaceId = params.workspaceId;
    if (paramWorkspaceId) {
      return paramWorkspaceId;
    }

    // Try to extract from pathname if not in params
    const pathSegments = location.pathname.split("/");
    const workspaceIndex = pathSegments.findIndex((segment) => segment === "workspace");
    if (workspaceIndex !== -1 && pathSegments[workspaceIndex + 1]) {
      return pathSegments[workspaceIndex + 1];
    }

    // Try to extract from pathname patterns like /workspace/123/project/456
    const workspaceIndex2 = pathSegments.findIndex((segment) => segment === "workspace");
    if (workspaceIndex2 !== -1 && pathSegments[workspaceIndex2 + 1]) {
      return pathSegments[workspaceIndex2 + 1];
    }

    return null;
  }, [params.workspaceId, location.pathname]);

  return {
    projectId,
    workspaceId,
    // Helper to check if we're in a project context
    isProjectContext: !!projectId,
    // Helper to check if we're in a workspace context
    isWorkspaceContext: !!workspaceId,
  };
};
